/* Choose your sets — set cards, "Customize your own" trigger card */

import space from '@amblique/king-living/app/theme/foundations/space';
import sizes from '@amblique/king-living/app/theme/foundations/sizes';
import {A11Y_FOCUS_VISIBLE} from '@amblique/king-living/app/theme/foundations/a11y-focus-visible';

export const chooseYourSetsParts = [
  'container',
  'popularSetsHeading',
  'setCardWrapper',
  'setCard',
  'setCardImageWrapper',
  'setCardImage',
  'setCardBadge',
  'setCardBadgeText',
  'setCardContent',
  'setCardName',
  'setCardEditLink',
  'setCardPriceRow',
  'setCardPriceColumn',
  'setCardSalePrice',
  'setCardOriginalPrice',
  'setCardChevron',
  'setExpandedWrapper',
  'setExpandedOptionRow',
  'setExpandedOptionLabel',
  'setExpandedOptionName',
  'customizeTriggerCard',
  'customizeTriggerContent',
  'customizeTriggerTitle',
  'customizeTriggerDescription',
  'customizeTriggerLink',
  'customizeTriggerImageWrapper',
  'customizeTriggerImage'
];

export const chooseYourSetsBaseStyle = {
  container: {
    marginTop: space[6],
  },
  popularSetsHeading: {
    color: 'grayAlpha.90',
    fontSize: {base: 'md', lg: 'lg'},
    fontWeight: 'medium',
    lineHeight: 1.2,
    marginBottom: space[4],
  },
  setCardWrapper: {
    bg: 'white',
    borderColor: 'grayAlpha.10',
    borderRadius: 'base',
    borderStyle: 'solid',
    borderWidth: '1.5px',
    boxShadow: 'sm',
    marginTop: 1.5,
    overflow: 'hidden',
    transition: 'border-color 0.15s ease, border-width 0.15s ease, background-color 0.15s ease',
    width: '100%',
    _hover: {
      bg: 'white',
      borderColor: 'grayAlpha.90',
      boxShadow: 'sm',
    },
    sx: {
      '&[data-selected="true"]': {
        borderColor: 'grayAlpha.90',
        borderWidth: '2px',
      },
      '&[data-selected="true"]:hover': {
        bg: 'white',
        borderColor: 'grayAlpha.90',
        borderWidth: '2px',
      },
      '& .selected-customize-item': {
        borderTop: 'none',
      },
    },
  },
  setCard: {
    alignItems: 'center',
    bg: 'white',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    minH: space[17.5],
    minW: sizes.chooseYourSetsSetCardMax,
    paddingLeft: 1.5,
    paddingRight: 5,
    paddingY: 1.5,
    gap: 4,
    width: '100%',
    _hover: {
      bg: 'white',
    },
    sx: {
      '& [role="radio"]': A11Y_FOCUS_VISIBLE,
      '& button[type="button"]': A11Y_FOCUS_VISIBLE,
    },
  },
  setCardImageWrapper: {
    borderRadius: '4px',
    flexShrink: 0,
    height: '64px',
    overflow: 'hidden',
    width: '64px',
  },
  setCardImage: {
    height: '100%',
    objectFit: 'cover',
    width: '100%',
  },
  setCardBadge: {
    bg: 'gray.800',
    borderRadius: '3px',
    left: '4px',
    paddingX: '3px',
    paddingY: '2px',
    position: 'absolute',
    top: '6px',
  },
  setCardBadgeText: {
    color: 'white',
    fontSize: '13px',
    fontWeight: 'semibold',
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
  },
  setCardContent: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: 2,
    justifyContent: 'center',
    minWidth: 0,
  },
  setCardName: {
    color: 'grayAlpha.90',
    fontSize: {base: '14px', lg: '16px'},
    fontVariantNumeric: 'lining-nums proportional-nums',
    fontWeight: 'semibold',
    lineHeight: 1.4,
    noOfLines: 2,
  },
  setCardEditLink: {
    color: 'brandColors.viewDetailsLink',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 'semibold',
    lineHeight: 1.2,
    textDecoration: 'underline',
    textDecorationSkipInk: 'auto',
    textDecorationStyle: 'solid',
    textUnderlineOffset: 'auto',
    textUnderlinePosition: 'from-font',
    width: 'fit-content',
    _hover: {
      color: 'grayAlpha.90',
    },
  },
  setCardPriceRow: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    gap: 3,
    justifyContent: 'flex-end',
    minWidth: 0,
  },
  setCardPriceColumn: {
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    gap: '6px',
    textAlign: 'right',
  },
  setCardSalePrice: {
    color: 'grayAlpha.90',
    fontSize: {base: '13px', lg: '12px'},
    fontVariantNumeric: 'lining-nums proportional-nums',
    fontWeight: 'semibold',
    lineHeight: 1.2,
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  setCardOriginalPrice: {
    color: 'greyscale.70',
    fontSize: {base: '13px', lg: '12px'},
    fontVariantNumeric: 'lining-nums proportional-nums',
    fontWeight: 'normal',
    lineHeight: 1.2,
    textAlign: 'right',
    textDecoration: 'line-through',
    whiteSpace: 'nowrap',
  },
  setCardChevron: {
    alignItems: 'center',
    color: 'brandColors.primaryDarkLinen',
    display: 'inline-flex',
    flexShrink: 0,
    h: '16px',
    justifyContent: 'center',
    lineHeight: 1,
    w: '16px',
  },
  setExpandedWrapper: {
    bg: 'white',
  },
  setExpandedOptionRow: {
    alignItems: 'baseline',
    justifyContent: 'space-between',
    paddingY: '4px',
  },
  setExpandedOptionLabel: {
    color: 'greyscale.70',
    flexShrink: 0,
    fontSize: '12px',
    fontWeight: 'normal',
    lineHeight: 1.4,
    marginRight: '12px',
  },
  setExpandedOptionName: {
    color: 'grayAlpha.70',
    fontSize: '12px',
    fontWeight: 'medium',
    lineHeight: 1.4,
    textAlign: 'right',
  },
  customizeTriggerCard: {
    bg: 'white',
    borderRadius: '4px',
    boxShadow: 'sm',
    cursor: 'pointer',
    display: 'flex',
    transition: 'border-color 0.15s ease',
    width: '100%',
    _hover: {
      bg: 'gray.50',
      boxShadow: 'sm',
    },
  },
  customizeTriggerContent: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: '8px',
    justifyContent: 'center',
    minWidth: 0,
    padding: '8px 8px 8px 20px',
  },
  customizeTriggerTitle: {
    sx: {
      '& h1, & h2, & h3, & h4, & h5, & h6': {
        color: 'grayAlpha.90',
        fontFamily: 'body',
        fontSize: {base: '14px', lg: '16px'},
        fontStyle: 'normal',
        fontVariantNumeric: 'lining-nums proportional-nums',
        fontWeight: 600,
        lineHeight: 1.4,
        margin: 0,
      },
      '& p': {
        color: 'grayAlpha.70',
        fontFamily: 'body',
        fontSize: {base: '13px', lg: '12px'},
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: 1.2,
        margin: 0,
      },
      '& h1 + p, & h2 + p, & h3 + p, & h4 + p, & h5 + p, & h6 + p': {
        marginTop: '8px',
      },
    },
  },
  customizeTriggerDescription: {
    margin: 0,
    padding: 0,
    sx: {
      '& .chakra-text': {
        marginTop: '2px',
      },
    },
  },
  customizeTriggerLink: {
    color: 'brandColors.primaryDarkLinen',
    fontFamily: 'body',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: 1.2,
    textAlign: 'left',
    textDecoration: 'underline',
    sx: {
      textDecorationSkipInk: 'auto',
      textDecorationThickness: 'auto',
      textUnderlineOffset: 'auto',
      textUnderlinePosition: 'from-font',
    },
  },
  customizeTriggerImageWrapper: {
    flexShrink: 0,
    height: '133px',
    overflow: 'hidden',
    width: '140px',
  },
  customizeTriggerImage: {
    height: '100%',
    objectFit: 'cover',
    width: '100%',
  },
};
